/**
 * Logic module for the Conference Room Designer.
 * This module contains functions for managing and resetting area states.
 * @module conferenceRoomDesignerLogic
 */
import initialState from "toolConstants/conferenceroomDesigner/initalState";
import constants from "./constants";
import { ceil, find, findIndex, has, keys, max, mergeWith, some } from "lodash";
import equipmentCatalog from "globalConstants/EquipmentCatalog";


/**
 * Resets an area to its initial state, with the option to reset specific parameters.
 * 
 * @function
 * @param {Object} container - The container object holding all areas.
 * @param {string|null} [parameter=null] - Specific parameter to reset. If null, resets all parameters.
 * 
 * @description
 * This function resets the active area in the container to its initial state.
 * It preserves the current ceiling tile selection while resetting other ceiling tile properties.
 * Other area properties may be reset based on the implementation (not shown in the provided code).
 * 
 * @example
 * // Reset all parameters of the active area
 * resetArea(containerState);
 * 
 * // Reset only a specific parameter (e.g., 'table')
 * resetArea(containerState, 'table');
 */
export const resetArea = (container, parameter = null, value = null) => {
  const activeArea = container.areas[container.activeTab];
  const initState = initialState.area();
  initState.id = activeArea.id;
  initState.name = activeArea.name;

  const { table: { shape }, microphones: { model: micModel }, speakers: { model: spkrModel }, customized } = activeArea;
  const { microphones: isMicCustom = false, speakers: isSpkrCustom = false, cameras: isCameraCustom = false } = customized
  const { microphones: { [micModel.replace('mic', '_mic')]: { coverage: micCoverage } }, speakers: { [spkrModel]: { coverage: spkrCoverage } } } = constants.equipment

  // Replace arrays entirely instead of merging by index
  const customizer = (objValue, srcValue) => {
    if (Array.isArray(srcValue)) {
      return srcValue;
    }
  };

  // Reset devices helper function 
  const resetDevices = (resetMics = true, resetSpkrs = true, resetCameras = true, changes = {}) => {
    // Reset device customizations
    const deviceReset = {
      qty: 0,
      layout: [],
      plotData: null,
    };
    mergeWith(activeArea, {
      ...resetMics && {
        microphones: {
          ...deviceReset,
          coverage: micCoverage(activeArea),
          ...changes.microphones
        }
      },
      ...resetSpkrs && {
        speakers: {
          ...deviceReset,
          coverage: spkrCoverage(activeArea),
          ...changes.speakers,
        }
      },
      ...resetCameras && {
        cameras: {
          ...deviceReset,
          ...changes.cameras
        }
      },
      customized: {
        microphones: resetMics ? false : activeArea.customized.microphones,
        speakers: resetSpkrs ? false : activeArea.customized.speakers,
        cameras: resetCameras ? false : activeArea.customized.cameras
      }
    }, customizer)
  };

  // Non device reseters 
  switch (parameter) {
    case 'dimensions':
      // Reset ceiling tiles only when room dimensions change 
      const { tile } = activeArea.ceilingTiles;
      const ceilingTiles = initState.ceilingTiles;
      activeArea.ceilingTiles = { ...ceilingTiles, tile: tile };

    // Intentional fall through
    case 'ceilingHeight':
      resetDevices();
      break;

    case 'table':
      if (Boolean(shape)) {
        resetDevices(!isMicCustom, !isSpkrCustom, true)
      }
      break;

    case 'ceilingTiles':
    case 'acoustics':
      resetDevices(!isMicCustom, !isSpkrCustom, isCameraCustom)
      break;

    case 'microphones':
      const { model = null } = value;
      if (model) {
        const changes = {};
        const { microphones: { [model.replace('mic', '_mic')]: { isBarMic = false, isVideoBar = false } } } = constants.equipment;
        changes.microphones = {
          height: constants.equipment.microphones[model.replace('mic', '_mic')].height(activeArea),
          heightRange: constants.equipment.microphones[model.replace('mic', '_mic')].heightRange(activeArea),
          coverage: constants.equipment.microphones[model.replace('mic', '_mic')].coverage(activeArea),
        }

        // Current speaker
        const speakerModel = activeArea.speakers.model;
        const { speakers: { [speakerModel]: { isBarSpkr = false } } } = constants.equipment;
        const newSpeakerModel = (isBarMic && !isBarSpkr) ? 'barspkr' : (!isBarMic && isBarSpkr) ? 'ceilingspkr' : speakerModel;
        const deviceHeight = constants.equipment.speakers[newSpeakerModel].height(activeArea);
        const deviceCoverage = constants.equipment.speakers[newSpeakerModel].coverage(activeArea);
        if (newSpeakerModel !== speakerModel || (isSpkrCustom && isBarSpkr)) {
          changes.speakers = { model: newSpeakerModel, height: deviceHeight, coverage: deviceCoverage };
        };
        // current camera 
        const cameraModel = activeArea.cameras.model;
        const { cameras: { [cameraModel]: { isBarCamera } = { isBarCamera: false } } } = constants.equipment;
        const newCameraModel = (isVideoBar && !isBarCamera) ? 'barCamera' : (!isVideoBar && isBarCamera) ? 'standAlone' : cameraModel;
        if (newCameraModel !== cameraModel) {
          changes.cameras = { model: newCameraModel, hasCamera: true };
        };
        resetDevices(true, 'speakers' in changes, 'cameras' in changes, changes)

      } else if (some(['height', 'coverage'], param => has(value, param))) {
        resetDevices(true, false, false)
      };
      break;

    case 'speakers':
      if (has(value, 'coverage')) {
        resetDevices(false, true, false)
      };
      break;

    default:
      if (value) {
        resetDevices(value === 'microphones', value === 'speakers', value === 'cameras');
      } else {
        container.areas[container.activeTab] = initState;
      }
  };

};

/**
* @function calculateEquipmentTable
* @description Calculates comprehensive equipment list for conference room system.
* 
* @param {Array<Object>} areas - Array of area objects (currently uses first area)
* @returns {Array<Object>} Array of equipment entries with quantities and details
* 
* @description
* This function calculates equipment needs based on room configuration:
* 
* 1. Conferencing Bar Systems:
*    - Handles all-in-one bar systems
*    - Includes associated accessories
* 
* 2. DSP-Based Systems:
*    - Calculates DSP requirements
*    - Determines microphone configurations:
*      * Base units and expanders
*      * Ceiling, pendant, and tabletop variations
*    - Handles speaker requirements:
*      * Amplification needs
*      * Speaker distribution
*    - Network infrastructure:
*      * TC-5 or AVB switch requirements
*      * Port calculations
*    - Camera integration
*    - Accessories and mounting solutions
* 
* 3. Cabling Requirements:
*    - Calculates different cable lengths needed
*    - Accounts for included cables in bundles
*    - Optimizes cable distribution
* 
* @example
* const equipmentList = calculateEquipmentTable([{
*   hasBar: false,
*   microphones: { modelName: 'TCM-X', layout: { length: 4 }},
*   speakers: { modelName: 'PM4-IC', layout: { length: 6 }},
*   cameras: { modelName: 'CAM-USB', layout: { length: 1 }},
*   productFamily: 'Parle'
* }]);
* 
* @note
* - Currently processes single area (first in array)
* - Uses equipment catalog for product specifications
* - Handles both all-in-one and component-based systems
* - Includes sophisticated port and cable calculations
* - Accounts for different mounting and installation scenarios
*/
export const calculateEquipmentTable = (areas) => {
  const area = areas[0]
  const { hasBar = false, microphones: { modelName: micModel, layout: { length: micQty } }, speakers: { modelName: spkrModel, layout: { length: spkrQty } }, cameras: { modelName: cameraModel, layout: { length: cameraQty } }, productFamily } = area;

  const equipmentList = new Map();

  // Helper function to add devices to equipment list
  const addToEquipmentList = (input, defaultQty = 1) => {
    // Normalize input to array format
    const items = Array.isArray(input) ? input : [[input, defaultQty]];
    items.forEach(([product, qty]) => {
      if (product) {
        const tableObject = makeTableObject(product, qty || defaultQty);
        if (tableObject) {
          const [key, value] = tableObject;
          equipmentList.set(key, value);
        };
      };
    });
  };

  // Helper to add cables
  const cables = equipmentCatalog.Cables.reduce((acc, curr) => {
    acc[curr.length] = 0;
    return acc
  }, {});

  const addCables = (arr) => {
    keys(cables).forEach((length, index) => cables[length] += arr[index]);
  };

  // Check if room has a bar 
  if (hasBar) {
    const bar = find(equipmentCatalog.ConferencingBars, bar => bar.modelName === micModel);
    addToEquipmentList(bar);
  } else {
    // Calculate DSP based system
    let micCount = micQty;
    let spkrCount = spkrQty;
    let dspCount = 0;

    // Calculate initial bundle
    const bundle = getBundle(productFamily, micModel, micCount, spkrModel, spkrCount);
    if (bundle) {
      addToEquipmentList(bundle);
      micCount -= bundle.includes.micQty;
      spkrCount -= bundle.includes.spkrQty;
      dspCount += 1;
    } else {
      // find dsps
      const processors = getProcessors(productFamily, micCount);
      addToEquipmentList(processors);
      dspCount += processors.reduce((acc, curr) => acc += curr[1], 0);
      // Add short cables between DSP and one long cable to switch/laptop
      addCables([dspCount - 1, 1, 0]);
    };

    // Calculate mic base units 
    const { isPendant = false, isTableTop = false } = find(equipmentCatalog.Microphones, mic => mic.modelName.includes(micModel));
    const isCeiling = !isPendant && !isTableTop;
    const baseUnits = ceil(micCount / (isPendant ? 3 : 2));

    // calculate amplification 
    const requiredAmps = ceil(spkrCount / 4);
    let ampBaseUnits = !isTableTop ? Math.min(requiredAmps, baseUnits) : 0;
    let noAmpUnits = baseUnits - ampBaseUnits;
    let ampQty = 0;

    if (requiredAmps - ampBaseUnits > 0) {
      // calculate needed AMP-450P units
      ampQty = ceil((spkrCount - ampBaseUnits * 4) / 8)
      // check if the AMP-450P is sufficient
      const coveredSpeakers = ampQty * 8;
      if (spkrCount <= coveredSpeakers && ampBaseUnits > 0) {
        noAmpUnits += ampBaseUnits;
        ampBaseUnits = 0;
      };
    };
    const expanderUnits = micCount - baseUnits;
    // Add extra microphones 
    const microphones = getMicrophones(micModel, ampBaseUnits, noAmpUnits, expanderUnits);
    addToEquipmentList(microphones);
    // Add 10' for each expander unit (except tabletop) and between base unit and mic for TCM-X. Add a 25' cable for each base unit
    addCables([0, (isTableTop ? 0 : expanderUnits + (isPendant ? 0 : baseUnits)), baseUnits])

    // Add extra speakers 
    if (spkrCount > 0) {
      const speakers = find(equipmentCatalog.Speakers, spkr => spkr.modelName.includes(spkrModel));
      addToEquipmentList(speakers, spkrCount);
    };

    // Add extra amps if required
    if (ampQty > 0) {
      const amplifiers = find(equipmentCatalog.Amplifiers, amp => amp.modelName.includes('AMP-450'));
      addToEquipmentList(amplifiers, ampQty);
      // AMP-450BP comes with a short jumper for the speaker it's attached to.
      addCables([0, -ampQty, ampQty]);
    };

    // Add TC-5 or AVB switches
    const totalDevicesNeedingPorts = (dspCount - 1) + ampBaseUnits + noAmpUnits + ampQty;  // all devices requiring connection
    const totalAvailablePorts = 4 * dspCount;  // all ports provided by DSPs
    const requiredExtraPorts = totalDevicesNeedingPorts - totalAvailablePorts;

    if (requiredExtraPorts <= 13) {
      const tcUnits = max([0, ceil(requiredExtraPorts / 3)]);
      if (tcUnits > 0) {
        const TC5 = find(equipmentCatalog.Switches, swch => swch.modelName.includes('TC-5'));
        addToEquipmentList(TC5, tcUnits);
        addCables([tcUnits, 0, 0]);
      }
    } else {
      const switches = getSwitches(requiredExtraPorts);
      addToEquipmentList(switches);
      const switchQty = switches.reduce((acc, curr) => acc += curr[1], 0)
      addCables([switchQty, 0, 0]);
    };

    // Add camera
    const camera = find(equipmentCatalog.Cameras, camera => camera.modelName === cameraModel);
    addToEquipmentList(camera)



    // Add accesories

    // Calculatge Speaker cables
    // Pendant mics don't have RJ-45 so we only need 8 * AMP-450 (others use speaker wire). Otherwise all speakers are wired with Cat5
    let spkrCables = isPendant ? 8 * ampQty : spkrCount;

    // Calculate BPAK
    // BPAK mounts on ceiling speaker so it can't be more than total speakers 
    const bpakQty = Math.min((isCeiling ? baseUnits : 0), spkrCount);


    // Adjust speaker cables - BPAK comes with a short adapter cable for the speaker it's mouted on. Only used in amplified versions  
    spkrCables -= (bpakQty > 0 ? ampBaseUnits : 0);
    addCables([0, spkrCables, 0]);

    // Add cables 
    const cablesToAdd = equipmentCatalog.Cables.filter(cable => cables[cable.length] > 0).map(cable => [cable, cables[cable.length]]);
    addToEquipmentList(cablesToAdd);

    // Add BPAK
    if (bpakQty > 0) {
      const bpak = find(equipmentCatalog.Accesories, acc => acc.modelName.includes('BPAK'));
      addToEquipmentList(bpak, bpakQty);
    };

  }



  return [...equipmentList.values()]
}

// HELPER Functions
/**
* @function makeTableObject
* @description Converts equipment data into standardized table entry format.
* 
* @param {Object} equipment - Equipment data object
* @param {string} equipment.modelName - Model name of the equipment
* @param {string} [equipment.partNumber] - Part number for the equipment
* @param {string} equipment.description - Description of the equipment
* @param {number} [qty=1] - Quantity of the equipment
* @returns {Array|null} Array containing [model, tableData] or null if equipment is empty
* 
* @description
* Creates table entry by:
* 1. Validating equipment data
* 2. Generating product link if part number exists
* 3. Formatting data into standard table structure
* 
* @example
* const result = makeTableObject({
*   modelName: 'TCM-X',
*   partNumber: '123456',
*   description: 'Ceiling microphone'
* }, 2);
* 
* // Returns [
* //   'TCM-X',
* //   {
* //     qty: 2,
* //     product: {
* //       name: 'TCM-X',
* //       link: 'https://products.biamp.com/product-details/-/o/ecom-item/123456'
* //     },
* //     description: 'Ceiling microphone'
* //   }
* // ]
* 
* @note
* - Returns null if equipment array is empty
* - Link is only generated if part number is available
* - Uses Biamp product URL structure
*/
const makeTableObject = (equipment = [], qty = 1) => {
  if (equipment.length === 0) return null;
  const model = equipment.modelName;
  const partNumber = equipment?.partNumber ?? null;
  const link = partNumber ? `https://products.biamp.com/product-details/-/o/ecom-item/${partNumber}` : null;
  const description = equipment.description;
  return [model, { qty, product: { name: model, link }, description }]
};

/**
* @function getBundle
* @description Determines appropriate equipment bundle based on room requirements.
* 
* @param {string} family - Product family (e.g., 'Devio')
* @param {string} micModel - Microphone model
* @param {number} micQty - Number of microphones needed
* @param {string} spkrModel - Speaker model
* @param {number} spkrQty - Number of speakers needed
* @returns {Object|null} Bundle object if suitable match found, null otherwise
* 
* @description
* Selects appropriate bundle by:
* 1. Validating quantity limits (max 4 mics, 16 speakers)
* 2. Determining microphone type and requirements:
*    - Tabletop: Medium bundle for 1 mic/≤2 speakers, Large otherwise
*    - Ceiling: Bundle selection based on speaker amplification needs
* 3. Evaluating speaker amplification strategy:
*    - Uses head amp for speaker counts 1-4, 9-12
*    - Defers to AMP-450BP for counts 5-8, 13-16 (more cost-effective)
* 
* @note
* - Returns null if quantities exceed limits
* - Bundle selection considers cost efficiency
* - Different logic for tabletop vs ceiling microphones
* - Optimizes for speaker amplification needs
*/
const getBundle = (family, micModel, micQty, spkrModel, spkrQty) => {
  if (micQty > 4 || spkrQty > 16) return null;
  const isTableTop = find(equipmentCatalog.Microphones, mic => mic.modelName.includes(micModel))?.isTableTop ?? false;
  // useHeadAmp logic: returns true for speaker counts 1~4, 9~12, 17~20, etc. Either bundle has a single amp which supports <=4 speakers. From 4~8 (and 13~16) it's more cost effective to use an AMP-450BP whicch is not in a bundle 
  const useHeadAmp = spkrQty % 8 > 0 && spkrQty % 8 <= 4;
  const [searchMicQty, searchSpkrQty] = isTableTop ?
    (micQty === 1 ? [1, 2] : [2, 4]) : // tabletop mic logic: Medium for 1 mic or ≤2 speakers, Large otherwise
    (!useHeadAmp ? [] : (micQty === 1 ? [1, 2] : [2, 4])); // Ceiling mic logic: Requires useHead to be true

  const bundle = find(equipmentCatalog.Bundles, bundle =>
    bundle.productFamily === family &&
    micModel.includes(bundle.includes.micModel) && searchMicQty <= bundle.includes.micQty &&
    spkrModel.includes(bundle.includes.spkrModel) && searchSpkrQty <= bundle.includes.spkrQty
  );
  return bundle
};

/**
* @function getProcessors
* @description Calculates minimum required DSP processors based on microphone count.
* 
* @param {string} family - Product family name
* @param {number} micCount - Total number of microphones to support
* @returns {Array<Array>} Array of processor pairs [processor, quantity]
* 
* @description
* Determines optimal DSP configuration by:
* 1. Starting with total microphone count
* 2. Iteratively selecting processors:
*    - Finds processor with closest matching capacity
*    - Defaults to 16-channel processor if no exact match
*    - Aggregates same processor models
* 3. Continues until all microphones are supported
* 
* @example
* const processors = getProcessors('Parle', 20);
* // For 20 microphones might return:
* // [
* //   [{ model: 'TesiraFORTÉ X 400', capacity: 16 }, 1],
* //   [{ model: 'TesiraFORTÉ X 100', capacity: 8 }, 1]
* // ]
* 
* @note
* - Optimizes for minimum number of processors
* - Considers processor capacity limits
* - Groups identical processor models
* - Maintains product family compatibility
*/
const getProcessors = (family, micCount) => {
  let counter = micCount;
  const processors = [];
  while (counter > 0) {
    const dsp = find(equipmentCatalog.Processors, dsp => dsp.productFamily === family && (counter <= dsp.capacity || dsp.capacity === 16));
    const index = findIndex(processors, processor => processor[0] === dsp);
    index >= 0 ? processors[index][1] += 1 : processors.push([dsp, 1]);
    counter -= dsp.capacity;
  };
  return processors
};

/**
* @function getMicrophones
* @description Generates list of required microphone units based on system configuration.
* 
* @param {string} micModel - Base microphone model
* @param {number} ampBaseUnits - Number of amplified base units needed
* @param {number} noAmpUnits - Number of non-amplified base units needed
* @param {number} expanderUnits - Number of expander units needed
* @returns {Array<Array>} Array of microphone pairs [microphone, quantity]
* 
* @description
* Builds microphone list by:
* 1. Processing three unit types:
*    - Amplified base units (with built-in amplification)
*    - Non-amplified base units (standard processing)
*    - Expander units (additional coverage)
* 2. Finding matching microphone models from catalog
* 3. Creating quantity pairs for each type
* 
* @example
* const mics = getMicrophones('TCM-X', 2, 1, 3);
* // Returns:
* // [
* //   [{ modelName: 'TCM-XA', isAmpUnit: true, ... }, 2],
* //   [{ modelName: 'TCM-X', isBaseUnit: true, ... }, 1],
* //   [{ modelName: 'TCM-XEX', isExpanderUnit: true, ... }, 3]
* // ]
* 
* @note
* - Only includes units with non-zero quantities
* - Matches microphones based on model and unit type
* - Uses equipment catalog for model lookup
* - Maintains product family consistency
*/
const getMicrophones = (micModel, ampBaseUnits, noAmpUnits, expanderUnits) => {
  const condition = ['isAmpUnit', 'isBaseUnit', 'isExpanderUnit'];
  return [ampBaseUnits, noAmpUnits, expanderUnits].reduce((mics, qty, index) => {
    if (qty > 0) {
      const mic = find(equipmentCatalog.Microphones, mic => mic.modelName.includes(micModel) && mic[condition[index]])
      mics.push([mic, qty])
    }
    return mics
  }, []);
};

/**
* @function getSwitches
* @description Calculates minimum required network switches based on AVB port requirements.
* 
* @param {number} AVBPorts - Total number of AVB ports needed
* @returns {Array<Array>} Array of switch pairs [switch, quantity]
* 
* @description
* Determines optimal switch configuration by:
* 1. Starting with total port requirement
* 2. Iteratively selecting switches:
*    - Finds switch with closest matching port count
*    - Defaults to 24-port switch if no exact match
*    - Aggregates same switch models
* 3. Continues until all port requirements are met
* 
* @example
* const switches = getSwitches(30);
* // For 30 ports might return:
* // [
* //   [{ modelName: 'AVB-SW-24', ports: 24 }, 1],
* //   [{ modelName: 'AVB-SW-8', ports: 8 }, 1]
* // ]
* 
* @note
* - Optimizes for minimum number of switches
* - Considers switch port capacity limits
* - Groups identical switch models
* - Similar logic to getProcessors but for network infrastructure
*/
const getSwitches = (AVBPorts) => {
  let counter = AVBPorts;
  const switches = [];
  while (counter > 0) {
    const swch = find(equipmentCatalog.Switches, swch => counter <= swch.ports || swch.ports === 24);
    const index = findIndex(switches, sw => sw[0] === swch);
    index >= 0 ? switches[index][1] += 1 : switches.push([swch, 1]);
    counter -= swch.ports;
  };
  return switches
};